import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import api, { extractData } from '../../utils/api';

interface EditorConfig {
  config: any;
  onlyofficeUrl: string;
}

const AttachmentEditor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailId = searchParams.get('emailId');
  const filename = searchParams.get('filename');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const docEditorRef = useRef<any>(null);

  useEffect(() => {
    if (!emailId || !filename) {
      setError('Missing emailId or filename');
      setLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        const res = await api.get(
          `/emails/${emailId}/attachments/${encodeURIComponent(filename)}/editor-config`,
        );
        const data = extractData<EditorConfig>(res);
        setEditorConfig(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to load editor');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [emailId, filename]);

  useEffect(() => {
    if (!editorConfig) return;

    const scriptId = 'onlyoffice-api-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    const initEditor = () => {
      if (docEditorRef.current) {
        docEditorRef.current.destroyEditor?.();
      }

      // @ts-ignore
      if (window.DocsAPI) {
        // @ts-ignore
        docEditorRef.current = new window.DocsAPI.DocEditor('attachment-editor', {
          ...editorConfig.config,
          width: '100%',
          height: '100%',
          events: {
            onAppReady: () => console.log('OnlyOffice editor ready'),
            onError: (event: any) => console.error('OnlyOffice error:', event),
          },
        });
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `${editorConfig.onlyofficeUrl}/web-apps/apps/api/documents/api.js`;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => setError('Failed to load OnlyOffice editor');
      document.head.appendChild(script);
    } else {
      initEditor();
    }

    return () => {
      if (docEditorRef.current) {
        docEditorRef.current.destroyEditor?.();
        docEditorRef.current = null;
      }
    };
  }, [editorConfig]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography
          component="a"
          href="#"
          onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(-1); }}
          sx={{ cursor: 'pointer', color: 'primary.main' }}
        >
          Go back
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          height: 40,
        }}
      >
        <Tooltip title="Back">
          <IconButton size="small" onClick={() => navigate(-1)}>
            <BackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" fontWeight={500} sx={{ ml: 1 }}>
          {filename || 'Attachment'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <div id="attachment-editor" ref={editorRef} style={{ width: '100%', height: '100%' }} />
      </Box>
    </Box>
  );
};

export default AttachmentEditor;
